import type { SessionUser } from '@momonos/shared'

/**
 * Admin session storage.
 *
 * Only the short-lived access token is kept in memory + sessionStorage; the
 * refresh token is an httpOnly cookie the JavaScript here cannot read, which is
 * the point of FR-AUTH-4. A stolen sessionStorage value expires in 15 minutes.
 */

const TOKEN_KEY = 'momonos.admin.token'
const USER_KEY = 'momonos.admin.user'
const BRANCH_KEY = 'momonos.admin.branch'

function storage(): Storage | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage
  } catch {
    return undefined
  }
}

export function loadToken(): string | null {
  try {
    return storage()?.getItem(TOKEN_KEY) ?? null
  } catch {
    return null
  }
}

export function saveToken(token: string | null): void {
  try {
    if (token) storage()?.setItem(TOKEN_KEY, token)
    else storage()?.removeItem(TOKEN_KEY)
  } catch {
    // A blocked storage just means signing in again on reload.
  }
}

export function loadUser(): SessionUser | null {
  try {
    const raw = storage()?.getItem(USER_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as SessionUser
    // Storage is user-writable: a hand-edited role here changes nothing on the
    // server, but it must not crash the shell either.
    return typeof parsed?.id === 'string' && typeof parsed?.role === 'string' ? parsed : null
  } catch {
    return null
  }
}

export function saveUser(user: SessionUser | null): void {
  try {
    if (user) storage()?.setItem(USER_KEY, JSON.stringify(user))
    else storage()?.removeItem(USER_KEY)
  } catch {
    // Non-fatal.
  }
}

/** FR-AUTH-6 — the branch the admin is currently looking at. */
export function loadBranch(): string | null {
  try {
    return storage()?.getItem(BRANCH_KEY) ?? null
  } catch {
    return null
  }
}

export function saveBranch(branchId: string | null): void {
  try {
    if (branchId) storage()?.setItem(BRANCH_KEY, branchId)
    else storage()?.removeItem(BRANCH_KEY)
  } catch {
    // Non-fatal.
  }
}

export function clearSession(): void {
  saveToken(null)
  saveUser(null)
  saveBranch(null)
}

/**
 * Which sections a role may see.
 *
 * The server enforces every one of these independently — this only decides what
 * to render, so a hidden nav item is a courtesy, not a security boundary.
 */
export const SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', roles: ['owner', 'admin', 'manager'] },
  { id: 'orders', label: 'Orders', roles: ['owner', 'admin', 'manager', 'cashier'] },
  { id: 'menu', label: 'Menu', roles: ['owner', 'admin', 'manager'] },
  { id: 'inventory', label: 'Inventory', roles: ['owner', 'admin', 'manager', 'inventory_staff'] },
  { id: 'customers', label: 'Customers', roles: ['owner', 'admin', 'manager'] },
  { id: 'campaigns', label: 'Campaigns', roles: ['owner', 'admin', 'manager'] },
  { id: 'intelligence', label: 'Intelligence', roles: ['owner', 'admin', 'manager'] },
  { id: 'assistant', label: 'Assistant', roles: ['owner', 'admin', 'manager'] },
  { id: 'activity', label: 'Activity', roles: ['owner', 'admin', 'manager'] },
] as const

export type SectionId = (typeof SECTIONS)[number]['id']

export function sectionsFor(role: string): Array<{ id: SectionId; label: string }> {
  return SECTIONS.filter((section) => (section.roles as readonly string[]).includes(role)).map(
    (section) => ({ id: section.id, label: section.label }),
  )
}

export function canSee(role: string, section: SectionId): boolean {
  return sectionsFor(role).some((entry) => entry.id === section)
}
